import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import shuffle from 'utils/shuffle';
import { Section, Container, Typography } from 'components/base';
import useDataContext from 'data/context/data';
import contentApi from 'data/api/content';

import { Head, Layout } from '../components/layout';

import ProductDetailPage from './product/[productId]';
import CategoryDetailPage from './category/[categoryId]';
import ContentPage, { CONTENT_PAGES } from './content/[contentId]';

import { ProductsSection } from '../components/custom/mainSections';
import { MIGHT_LIKE_PRODUCTS } from '../specifications/lists';

import PRODUCT_SPECIFICATION from '../specifications/products';
import PRODUCT_CATEGORIES from '../specifications/categories';
import { usesProductPageLayout } from '../lib/productPage.mjs';

const PAGE_TYPE = {
  NOT_FOUND: -1,
  PRODUCT: 0,
  CATEGORY: 1,
  CONTENT: 2,
};

const PRODUCT_URLS = Object.entries(PRODUCT_SPECIFICATION).reduce((acc, [id, { url }]) => {
  acc[url] = id;
  return acc;
}, {});

const CATEGORY_URLS = PRODUCT_CATEGORIES.reduce((acc, { id, url }) => {
  acc[url] = id;
  return acc;
}, {});

// Home > category > product, only on format 2 product pages whose content names a
// breadcrumbCategory that has a category page.
const productBreadcrumb = async content => {
  if (!usesProductPageLayout(content) || !content.breadcrumbCategory) return null;

  const category = PRODUCT_CATEGORIES.find(({ id }) => id === content.breadcrumbCategory);
  if (!category) return null;

  const categoryContent = await contentApi.category(category.id);

  return [
    { name: 'Home', url: '/' },
    { name: categoryContent.caption, url: category.url },
    { name: content.caption },
  ];
};

const DynamicPage = props => {
  const { pageType, ...pageProps } = props;

  const dataContext = useDataContext();

  const [mightLikeProducts, setMightLikeProducts] = useState([]);
  useEffect(() => {
    if (props.pageType === PAGE_TYPE.NOT_FOUND) {
      const products = dataContext.getProducts(MIGHT_LIKE_PRODUCTS);
      setMightLikeProducts(shuffle(products).slice(0, 6));
    }
  }, [props.pageType]);

  if (pageType === PAGE_TYPE.PRODUCT) {
    return <ProductDetailPage {...pageProps} />;
  }

  if (pageType === PAGE_TYPE.CATEGORY) {
    return <CategoryDetailPage {...pageProps} />;
  }

  if (pageType === PAGE_TYPE.CONTENT) {
    return <ContentPage {...pageProps} />;
  }

  return (
    <>
      <Head title="Page Not Found" noindex />

      <Layout>
        <Section>
          <Container className="text-center">
            <Typography.H4 className="mb-1">This page doesn’t exist.</Typography.H4>
            <Typography.P>Take a look at a few of our favorites instead.</Typography.P>
          </Container>
        </Section>

        <ProductsSection title="You might like" products={mightLikeProducts} xs={12} md={4} />
      </Layout>
    </>
  );
};

DynamicPage.getInitialProps = async ctx => {
  const params = ctx.query.params || [];

  const url = `/${params.join('/')}`;

  //
  // 1. ProductDetailPage
  //
  if (PRODUCT_URLS[url]) {
    const productId = PRODUCT_URLS[url];

    const content = await contentApi.product(productId);
    const breadcrumb = await productBreadcrumb(content);

    return {
      pageType: PAGE_TYPE.PRODUCT,
      productId,
      content,
      ...(breadcrumb ? { breadcrumb } : {}),
    };
  }

  //
  // 2. CategoryDetailPage
  //
  if (CATEGORY_URLS[url]) {
    const categoryId = CATEGORY_URLS[url];

    const content = await contentApi.category(categoryId);

    return {
      pageType: PAGE_TYPE.CATEGORY,
      categoryId,
      content,
    };
  }

  //
  // 3. ContentPage
  //
  if (CONTENT_PAGES[url]) {
    const contentId = CONTENT_PAGES[url];

    const { pageTitle, pageDescription, pageCaption, imageKey, body, bio } =
      await contentApi.singleType(contentId);

    return {
      pageType: PAGE_TYPE.CONTENT,
      contentId,
      imageKey,
      pageTitle,
      pageDescription,
      pageCaption,
      body,
      bio,
    };
  }

  if (ctx.res) {
    ctx.res.statusCode = 404;
  }

  return { pageType: PAGE_TYPE.NOT_FOUND, is404: true };
};

DynamicPage.propTypes = {
  pageType: PropTypes.number,
};

DynamicPage.defaultProps = {
  pageType: -1,
};

export default DynamicPage;
